import { unwrapResult } from '@reduxjs/toolkit'
import toast from 'cogo-toast'
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react'

import { useAppDispatch } from 'store'

import { authActions } from 'store/slices/auth'

interface IProps {
	children: React.ReactChild
}

type AppContextType = {
	isUnifiedDashboard?: boolean
	refreshAppContext: () => Promise<{ isUnifiedDashboard: boolean }>
}

const AppContext = createContext<AppContextType>({
	isUnifiedDashboard: undefined,
	refreshAppContext: async () => ({ isUnifiedDashboard: false }),
})

export default ({ children }: IProps) => {
	const dispatch = useAppDispatch()
	const [isUnifiedDashboard, setIsUnifiedDashboard] = useState<boolean>()
	
	const getUnifiedDashboard = useCallback(async () => {
		// We cannot use the React Router location here, as this may be called
		// before it is initialized
		const query = new URLSearchParams(globalThis.location?.search);
		try {
			return unwrapResult(await dispatch(authActions.useUnifiedLogin(query.get('user_id') || '')))
		} catch (error) {
			if (error instanceof Error) {
				toast.error(error.message)
			}
			return false
		}
	}, [dispatch])

	useEffect(() => {
		getUnifiedDashboard().then(setIsUnifiedDashboard)
	}, [getUnifiedDashboard])

	const refreshAppContext = async () => {
		const isUnifiedDashboard = await getUnifiedDashboard();
		setIsUnifiedDashboard(isUnifiedDashboard);
		return { isUnifiedDashboard };
	}

	const contextValue = useMemo(
		() => ({
			isUnifiedDashboard,
			refreshAppContext,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isUnifiedDashboard]
	)

	return (
		<AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
	)
}

export const useAppContext = () => useContext(AppContext)
